var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var VitesseAiSurface_1;
import { LitElement, html, nothing } from 'lit';
import { rememberConversation, saveDraft } from './session-memory';
import { indexBranches, positionAt } from './branches';
import { artifactOf } from './places';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import './chat';
import './run-tree-view';
import './run-pipes-view';
import { CHAPTER_FLOOR_TURNS } from './conversation';
import { partialEvents, runPartial as livePartial, EMPTY } from './run-partials';
import { FINISHED, runState, runElapsed, runCost, runTokens, runLabel, forestUsage, fmtTokenCount, fmtUsd } from './run-format';
import { pendingAsk, onAskChange, answerAsk, cancelAsk } from './ask-user';
import { renderMarkdown } from './markdown';
import { copyCode } from './turn-view';
import { transcriptSkeleton } from './turn-view';
import { headLead } from './head-lead';
import '../elements/vitesse-source-viewer';
import '../elements/vitesse-pill-tabs';
import { viewerTabs, viewerTab } from '../elements/vitesse-source-viewer';
import { statusBadge } from '../components/templates';
import { conversationLabel } from './conversation-list';
import { live, LiveTopic } from './live';
import '../elements/vitesse-button';
import { RunStatus } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
/*
 * <vitesse-ai-panel> — the AI rail body, and the router between its three surfaces.
 *
 * A thin renderer of the AiManager's session, subscribed via the manager pattern
 * (addEventListener('change') → requestUpdate). LIGHT DOM so the shell's tokens/SCSS resolve.
 *
 * THE THREE SURFACES, and why the default is what it is:
 *
 *   COMPOSER (default) — an empty conversation, ready to take a sentence. Opening the rail is
 *     overwhelmingly "I want to ask something", so that costs nothing: no list to get past, no
 *     button to press first. It IS the panel.
 *   CHAT — a thread, once one is open.
 *   HUB — one step back: earlier threads, memories, the console. The rarer intent, one control away.
 *
 * The panel owns only WHICH surface is showing. Conversation data lives in the manager's store, and
 * the composer's per-message choices live in the composer — so this file can route without knowing
 * anything about paging or model selection.
 */
let VitesseAiSurface = class VitesseAiSurface extends LitElement {
    constructor() {
        super(...arguments);
        /** The signed-in person's first name, for the composer's greeting. Absent is fine. */
        this.userName = '';
        /** `full` is the column being worked in; `context` is a place it was reached through, drawn
         *  lighter — collapsed thoughts and tool rows, no composer. */
        this.density = 'full';
        /*
         * WORK CONTINUES WHEN THE PANEL IS CLOSED — the rail collapses to zero width, it does not unmount
         * what is inside it — so the header needs to be told, or there is no way to know from outside.
         *
         * Announced as an event rather than read off the manager, because the conversation is the thing
         * that knows, and it lives here. The shell listens and decides what to draw; this only reports.
         */
        this.onChange = () => this.requestUpdate();
        /*
         * ACTIVITY IS THE CONVERSATION'S, NOT THE MANAGER'S.
         *
         * This was subscribed to the manager, which never fires when a turn starts or ends — so the
         * header's working indicator was wired end to end and simply never got told. The status belongs
         * to the open conversation, so the listener goes there, on the object that changes.
         */
        /*
         * WHO IS REPORTING, NOT JUST WHAT. A stack draws up to three of these at once and they each hold
         * a different thread, so a single shared boolean is whatever the last one to speak said: a
         * context column finishing cleared the flag while the column beside it was still working. The id
         * lets the shell keep a SET and ask whether it is empty, which is the question it actually has.
         */
        this.reporterId = `s${Math.random().toString(36).slice(2)}`;
        this.lastBusy = false;
        this.onConvChange = () => {
            // A stopped agent is not working, whatever this tab has left over — the same rule the composer
            // reads, so the header's indicator and the thread can never disagree. See `runFinished`.
            const busy = (this.conversation?.busy ?? false) && !this.conversation?.runFinished;
            if (busy === this.lastBusy)
                return;
            this.lastBusy = busy;
            this.announceBusy(busy);
        };
        this.watched = null;
        /**
         * A WATCHED THREAD DOES NOT STREAM — so listen to the one broadcast that already exists.
         *
         * A sub-agent's conversation is written by another driver. There is no live channel for a single
         * transcript (LIVE_TOPIC_TRANSCRIPTS is the conversation LIST), so what this view showed was a
         * snapshot taken when it opened: an agent could fail a write while it was on screen and the
         * failure appeared only after leaving the page and coming back.
         *
         * AUDITS is broadcast on every step of every run — and nothing was listening to it. This is the
         * listening half. It says only "a run advanced", which is exactly the frame's contract ("this only
         * says re-fetch X"), so the answer is to re-read the thread rather than to guess what changed.
         *
         * Armed only for a RUN's thread: the one this tab is driving is ahead of the archive, and
         * re-reading it would replace live turns with a copy that has not caught up.
         */
        this.unwatchAudits = null;
        /** The run moved. Its status and its clock are in the header, so redraw. */
        this.onRunChange = () => this.requestUpdate();
        /** A driven run wrote another chunk. Nothing else changed, so this is only a repaint — and it is
         *  what turns the registry into a live view rather than a value read once. */
        this.onPartialChange = () => this.requestUpdate();
        this.watchedTree = null;
        /** The plane named the open thread; show it. */
        /** The segmenter found chapters for a thread. Reload them if it is the one on screen. */
        this.onChapters = (e) => {
            const id = e.detail.conversationId;
            if (id === this.conversation?.id)
                void this.loadChapters(id);
        };
        this.onTitled = (e) => {
            this.convTitle = e.detail.title;
        };
        /*
         * THE HEADER'S TITLE COMES FROM THE LIST, AND THE LIST RACES THE RESTORE.
         *
         * On a fresh page load the panel reopens the remembered thread while the recents list is still in
         * flight, so the lookup at open time found nothing and the header said "Untitled" — for a thread
         * with a perfectly good stored name — until the next rename event. Whether a reload showed the
         * name or not depended on which fetch won, which is why it looked like titles were not
         * persisting at all. So the lookup re-runs when the list lands: cheap (one find on a change
         * event), idempotent, and it only ever FILLS an empty header — a name delivered by `onTitled`
         * mid-session is newer than the list copy and must not be overwritten by it.
         */
        this.onListChange = () => {
            if (this.convTitle || !this.conversation)
                return;
            this.convTitle = this.manager?.conversations.conversations.find((c) => c.id === this.conversation?.id)?.title ?? '';
        };
        /*
         * A SECOND HAND FOR AN AGENT'S HEADER — the same one the tree canvas already has, and the header
         * did not.
         *
         * The elapsed figure is computed against Date.now() on every render, so it is always CORRECT; what
         * it lacked was a reason to be drawn. The panel repaints when a poll lands or a stream event
         * arrives, and those are irregular, so a reader watching a working agent saw the clock sit still,
         * jump four seconds, sit still, jump one. Reported as the figures "jumping around" rather than
         * going up cleanly — which is exactly what an irregular repaint of a continuous value looks like,
         * and it reads as an agent that keeps stalling.
         *
         * Not a second source of truth: the VALUE still comes from the run's own start and end stamps.
         * This only decides how often it is drawn. Runs while a run on screen is live and stops when it is
         * not, so a finished agent — a document — is not repainted forever.
         */
        this.clock = null;
        /** The draft answer to a pending ask_user question, kept here so typing survives re-renders. */
        this.askDraft = '';
        /** The open thread, or null while the user is composing or at the hub. */
        this.conversation = null;
        /** Set while `open()` is in flight — resuming fetches a page of history first. */
        this.opening = false;
        /**
         * The agent run being READ, if any — the fourth surface.
         *
         * A run id rather than a conversation id, because that is what the tree hands over and what
         * addresses the run's own record. Stepping into an agent used to REPLACE the open thread with the
         * agent's conversation, which loses the place the user was reading and re-engages a run that had
         * stopped. Now it opens the record on top, and talking to the agent is a button inside it.
         */
        this.runId = '';
        /**
         * The tree being BROWSED — the fifth surface, and the map for the fourth.
         *
         * A transcript walks DOWN one agent at a time, so a reader several levels in could see rooms and
         * never the building. This shows the whole tree a run belongs to, and a node opens that run's
         * record — so tree → node → record → tree is a loop rather than a one-way walk.
         */
        this.treeRootRunId = '';
        /*
         * WHICH VIEW OF THE TREE — the canvas or the ordered rows.
         *
         * Pipes is the default because the first question about a fleet of agents is what SHAPE it is,
         * and that is the one a list cannot answer. Trace is a click away for the other question. Held on
         * the panel rather than inside either view so stepping into a node and coming back returns you to
         * the view you were reading, which is the whole reason a person picked one.
         */
        this.treeView = 'pipes';
        /** The open thread's name, for the chat header. Read from the store when a thread is resumed; a
         *  brand-new one has none until the server titles it. */
        this.convTitle = '';
        /** A one-line failure the reader has to be told about, cleared by the next action that could
         *  cause one. Only the optimistic delete sets it today. */
        this.notice = '';
        /**
         * Leaving a thread on purpose — the default back for a top-level chat.
         *
         * BACK GOES TO THE LIST, not to a blank composer. Clearing the conversation alone falls through
         * to renderComposer(), so stepping out of a thread landed the user in a NEW chat — the one place
         * they had not asked to go, and with the thread they just left nowhere in sight. `deleteOpen`
         * already ends at the hub for the same reason; back is the same intent without the deletion.
         */
        this.leaveThread = () => {
            // Leaving a thread on purpose is the one case where it should NOT reopen next time.
            if (this.manager)
                rememberConversation(this.manager.surface, '');
            this.manager?.close();
            this.conversation = null;
            this.watchConversation(null);
            this.watchRunAudits('');
            this.nav?.leave();
        };
        /** Agent threads this tab has already asked to segment. Each ask is a billed call, and reopening
         *  an agent must not buy contents it already has. */
        this.segmented = new Set();
        this.choose = async (conversationId, model, effort, expertId) => {
            if (!this.manager)
                return;
            this.opening = true;
            try {
                this.conversation = await this.manager.open(conversationId, { model });
                this.watchConversation(this.conversation ?? null);
                /*
                 * Seed the history when a thread is opened from OUTSIDE it (the recents list, a new chat).
                 * `apply` reaches here too when going back to a thread, and must not record — it would push
                 * the place it is currently restoring and the reader could never get past it. Recording only
                 * when the id differs from the place we are already on is what separates the two callers
                 * without threading a flag through.
                 */
                /*
                 * The version switcher is built from the recents list, and that list only loaded when somebody
                 * hovered Back or opened the hub — so branching from a thread you had opened directly left the
                 * index empty and no switcher appeared anywhere. Opening a thread is exactly the moment its
                 * versions become relevant, so the list is pulled then.
                 *
                 * Speculative and non-blocking: it never throws, and the thread opens whether or not it lands.
                 */
                this.manager.conversations.prefetch();
                // Alongside, not before: chapters are navigation, and a thread must open whether or not it
                // has any. A failure here leaves the button absent, which is the same as having none.
                this.chapters = [];
                if (this.conversation)
                    void this.loadChapters(this.conversation.id);
                // Applied AFTER open, on the conversation itself, because effort is per-turn state the thread
                // owns — the manager's job is to hand back the thread, not to hold every knob that shapes a
                // turn. Only when the composer actually chose one, so resuming a thread keeps its own.
                if (this.conversation && effort)
                    this.conversation.effort = effort;
                // Same rule for who ANSWERS: applied after open, only when one was actually
                // chosen, so resuming a thread keeps whoever it already had.
                if (this.conversation && expertId !== undefined)
                    this.conversation.expertId = expertId;
                this.convTitle = conversationId ? (this.manager.conversations.conversations.find((c) => c.id === conversationId)?.title ?? '') : '';
                // Remembered so a reload lands back in the thread rather than at the composer. Written on
                // open rather than on close, because a tab that is closed never gets to run cleanup.
                if (this.conversation)
                    rememberConversation(this.manager.surface, this.conversation.id);
            }
            finally {
                this.opening = false;
            }
        };
        /**
         * The composer committed a message: open a fresh thread and send it as the first turn.
         *
         * Sent from HERE rather than left to the chat view, so the message the user pressed Enter on is
         * the message that goes — a composer that handed its text to another surface to send would put a
         * render between the intent and the act, which is where dropped first messages come from.
         */
        /**
         * Delete the open thread and go back to the list.
         *
         * Straight to the list rather than back to the transcript of something that no longer exists —
         * and the manager refreshes the recents, so the row disappears with it.
         */
        this.deleteOpen = async () => {
            const id = this.conversation?.id;
            const manager = this.manager;
            if (!id || !manager)
                return;
            /*
             * LEAVE FIRST, DELETE SECOND.
             *
             * It awaited the server before doing anything at all, so pressing Delete on the one
             * irreversible control in the panel did nothing visible for a full round trip — long enough
             * that the reader presses it again. Deleting is a decision already made; the request is
             * bookkeeping, and bookkeeping does not belong between the press and the response.
             *
             * The failure path is what makes this safe to do optimistically: a delete that does not land
             * leaves the thread exactly where it was, and refreshing the list puts its row back in front of
             * the reader — so recovery is the truth reappearing rather than a silent loss.
             */
            this.conversation = null;
            this.convTitle = '';
            this.notice = '';
            this.nav?.leave();
            const ok = await manager.deleteConversation(id).catch(() => false);
            if (!ok) {
                this.notice = t('ai.deleteFailed');
                void manager.conversations.refresh();
            }
        };
        /*
         * NO COMMENTS INSIDE THE OPENING TAG, and this is not a style preference.
         *
         * An HTML comment written BETWEEN attributes is not a comment to the parser — it is parsed as a
         * run of attributes (the comment opener itself, then `the=""`, `chat=""` …), and every binding after it is swallowed
         * with it. Three bindings were dead because of comments sitting in this tag: both model defaults
         * (so the pickers showed placeholders mid-conversation and the effort control never appeared,
         * being gated on knowing whether the model can reason) and the fork handler (so the fork action
         * was never offered, because the view only shows it when a handler exists).
         *
         * Nothing rendered wrong, nothing threw, and the template type-checked. So the notes live here,
         * above the element, and the tag holds only bindings:
         *
         *   defaults    — both pickers need them to name the current model rather than a placeholder.
         *   (onOpenRun was bound here and the chat never declared it, so it was dropped on every
         *                 render. Its note argued for opening a sub-agent's RECORD rather than its
         *                 conversation, on the grounds that the conversation re-engages a stopped run —
         *                 which opening does not do; it hydrates and sends nothing. Stepping in to TALK
         *                 to an agent is the point of the tree, so the chat routes a node to its
         *                 conversation via onOpenConversation, and the record stays reachable from the
         *                 canvas. Binding removed rather than given an invented caller.)
         *   onForkTurn  — fork LANDS YOU IN THE FORK. Copying and leaving the reader where they were
         *                 means the thing they asked for has to be found by hand in the recents list.
         */
        /**
         * Where the open thread sits among its versions, at one message.
         *
         * Derived from the recents list the panel already holds — the tree is those rows' provenance, so
         * this costs one index build per list change and a map read per turn, with no request of its own.
         * Rebuilt lazily and cached against the list identity: the list changes when a branch is made or
         * a page arrives, and re-indexing on every turn render would be the one place this got expensive.
         */
        this.branchIndex = null;
        /** The open thread's chapters. Loaded when a thread opens; empty for one that has none. */
        this.chapters = [];
        /**
         * WHAT AN AGENT MADE — its source, its declaration, and the thing itself.
         *
         * The artifact is the run's own return: `deliverable_json` is the expert's declared payload, and
         * for a widget author that payload IS the widget — the program, the config keys it reads, the
         * size it wants. Read out of the tree rather than fetched, so this column paints on the first
         * frame instead of showing a skeleton for a value the panel already holds.
         *
         * The LIVE tab is the host app's, through `artifactPreview`. Running a widget needs that app's own
         * trust boundary — its opaque-origin frame, its capability grant, its value fan-out — and none of
         * that belongs in a shared element. An app that cannot run one (the fleet view, the console)
         * passes nothing and the viewer is the documents, which is all those surfaces want from it.
         */
        /** Which document the viewer is showing. Owned HERE because the header draws the bar — see below. */
        this.viewerPick = '';
        /*
         * WHAT THE READER CHANGED, keyed by run and document.
         *
         * A review that cannot fix the typo it just found is half a review — so the source and the
         * declaration are editable, and what is typed lives HERE rather than being written back over the
         * run. A run's return is a record of what an agent did; it is not a draft, and quietly rewriting
         * it would make the transcript lie about its own history.
         *
         * So an edit is a proposal: it runs immediately in the live tab, which is the whole reason to make
         * one, and the way it becomes real is the same way anything else does — it goes to the assistant,
         * which can place it, hand it back to its author, or argue with it.
         *
         * Session-lived on purpose. A draft that survives a reload and silently diverges from the run
         * behind it is a second copy of the artifact with no way to tell which is which.
         */
        this.edits = {};
        /**
         * THE SAME EDITS, ONE BEAT LATER — and only the live tab reads them.
         *
         * A widget host remounts when its source changes, by design: a running widget cannot be handed
         * new code, it has to be started again. Fed every keystroke, that is a remount per character — the
         * preview tears itself down and rebuilds while the person is still typing the word, which is the
         * one moment they are looking at it.
         *
         * The documents are NOT debounced: the editor owns that text and lagging it would push stale
         * characters back into the box the reader is typing in.
         */
        this.settled = {};
        this.settleTimer = null;
        /** What the language service thinks of each edited document — reported by the host's editor. */
        this.problems = {};
        /** Said on the button rather than as a toast: the acknowledgement belongs where the click was. */
        this.copied = '';
    }
    static { VitesseAiSurface_1 = this; }
    announceBusy(busy) {
        this.dispatchEvent(new CustomEvent('vitesse-ai-activity', { detail: { busy, id: this.reporterId }, bubbles: true, composed: true }));
    }
    /** Follow whichever thread is open; a thread that is replaced must not keep reporting. */
    watchConversation(next) {
        if (next === this.watched)
            return;
        this.watched?.removeEventListener('change', this.onConvChange);
        this.watched = next;
        this.watched?.addEventListener('change', this.onConvChange);
        this.onConvChange();
    }
    /** How often a watched agent's thread re-reads itself. Fast enough that a step landing feels
     *  immediate, slow enough that a long run costs a handful of reads a minute. */
    static { this.WATCH_POLL_MS = 2000; }
    watchRunAudits(conversationId) {
        this.unwatchAudits?.();
        this.unwatchAudits = null;
        if (!conversationId)
            return;
        /*
         * BOTH HALVES OF AN AGENT, not just its words.
         *
         * The thread refreshes from the archive; the RUN — its status, why it stopped, when it ended —
         * came from one lookup at open time. Refreshing only the first left the header saying "Running"
         * with a clock still counting under a transcript that had plainly finished, which is the
         * complaint that reads as the whole view being frozen.
         */
        const transcript = this.transcript;
        transcript?.addEventListener('change', this.onRunChange);
        const pull = () => {
            void this.manager?.refreshWatched(conversationId);
            void transcript?.refreshRun();
        };
        const unsub = live.subscribe(LiveTopic.AUDITS, pull);
        // Now, not on the first tick: the thread was opened from its FIRST page, and a long run's
        // newest step is in its tail. Two seconds of the wrong end of a thread reads as the wrong thread.
        pull();
        /*
         * AND A POLL UNDERNEATH IT, because the broadcast is not a guarantee.
         *
         * The subscription above is the cheap path and it usually works. It is also a single point of
         * failure for the ONE surface a person sits and watches: an agent's thread is written by
         * another driver, so if that notification does not arrive — the stream dropped, the tab was
         * backgrounded through the reconnect, the room coalesced it away — the view has no other reason
         * to re-read, and shows its skeleton for as long as the agent runs. Observed: six minutes of
         * empty page under a header counting up, then the whole transcript at once when something else
         * finally forced a read.
         *
         * So the thing being looked at pulls for itself. Every two seconds, the same read the broadcast
         * asks for — and only while the run is UNFINISHED, so a settled agent costs nothing. The two
         * paths are idempotent and cannot conflict: both re-read the same archive and apply the same
         * turns, so an arriving broadcast just makes the next tick a no-op.
         */
        const tick = setInterval(() => {
            const run = transcript?.run;
            // No run yet means the lookup has not landed — poll on, that is exactly the blind window.
            if (run && (runState(run) === 'done' || runState(run) === 'stopped'))
                return;
            pull();
        }, VitesseAiSurface_1.WATCH_POLL_MS);
        this.unwatchAudits = () => {
            unsub();
            clearInterval(tick);
            transcript?.removeEventListener('change', this.onRunChange);
        };
    }
    /**
     * THE TREE ARRIVES AFTER THE SURFACE DOES, and the panel has to notice.
     *
     * The pipes element subscribes to the tree itself, so the CARDS appeared the moment the read
     * landed. What the panel computes — the origin conversation, read off the roots — was evaluated
     * once, at a render where there were no roots yet, and nothing re-ran it. So the canvas drew the
     * agents and never the conversation above them: correct code, never called a second time.
     *
     * Idempotent, because `apply` runs on every visit to the same surface.
     */
    watchTree() {
        const tree = this.manager?.runTree;
        if (!tree || tree === this.watchedTree)
            return;
        this.watchedTree?.removeEventListener('change', this.onRunChange);
        tree.addEventListener('change', this.onRunChange);
        this.watchedTree = tree;
    }
    createRenderRoot() {
        return this;
    }
    willUpdate(changed) {
        if (changed.has('manager')) {
            const prev = changed.get('manager');
            prev?.removeEventListener('change', this.onChange);
            prev?.removeEventListener('titled', this.onTitled);
            this.manager?.addEventListener('change', this.onChange);
            partialEvents.addEventListener('change', this.onPartialChange);
            this.manager?.addEventListener('titled', this.onTitled);
            prev?.removeEventListener('chapters', this.onChapters);
            this.manager?.addEventListener('chapters', this.onChapters);
            prev?.conversations?.removeEventListener('change', this.onListChange);
            this.manager?.conversations.addEventListener('change', this.onListChange);
            // A transcript of this surface's own: the stack shows several agents side by side.
            this.transcript = this.manager?.newRunTranscript();
        }
        if (changed.has('place') && this.place)
            void this.apply(this.place);
    }
    connectedCallback() {
        super.connectedCallback();
        this.askOff = onAskChange(() => this.requestUpdate());
    }
    /** Is the header showing something whose numbers are still moving? */
    get headerLive() {
        const run = this.transcript?.run;
        return Boolean(run) && runState(run) !== 'done' && runState(run) !== 'stopped';
    }
    tickClock() {
        if (this.headerLive) {
            if (!this.clock)
                this.clock = setInterval(() => (this.headerLive ? this.requestUpdate() : this.tickClock()), 1000);
            return;
        }
        if (this.clock)
            clearInterval(this.clock);
        this.clock = null;
        if (this.settleTimer)
            clearTimeout(this.settleTimer);
        this.settleTimer = null;
    }
    disconnectedCallback() {
        if (this.clock)
            clearInterval(this.clock);
        this.clock = null;
        this.watchRunAudits(''); // a subscription outliving its view is a leak that only shows as traffic
        /*
         * THE LAST WORD HAS TO BE SAID WHILE THERE IS STILL SOMEBODY TO SAY IT TO.
         *
         * A bubbling event from an element that has already left the DOM reaches nothing, and
         * `watchConversation(null)` below runs AFTER super — so a column closed mid-turn used to take
         * its "still working" with it and leave the header's indicator lit for the rest of the session
         * with no run behind it. Retracted here, on the way out, before the element is detached.
         */
        if (this.lastBusy) {
            this.lastBusy = false;
            this.announceBusy(false);
        }
        super.disconnectedCallback();
        this.watchConversation(null);
        this.watchRunAudits('');
        partialEvents.removeEventListener('change', this.onPartialChange);
        this.manager?.removeEventListener('change', this.onChange);
        this.manager?.removeEventListener('titled', this.onTitled);
        this.manager?.removeEventListener('chapters', this.onChapters);
        this.askOff?.();
    }
    /**
     * The ask_user prompt — shown when the AI has paused on a question, cleared when it is answered.
     *
     * MINIMAL, deliberately: this is the wiring (a question, an optional set of quick choices, a
     * free-text reply, Enter to send) so the pause/resume works end to end and can be tested. The
     * visual treatment is a later pass — it wants to sit in the thread like a message, not float as a
     * bar, and that is a styling job on top of a mechanism that already works.
     */
    renderAsk() {
        const ask = pendingAsk();
        if (!ask)
            return nothing;
        /*
         * ONLY IN THE THREAD THAT ASKED.
         *
         * The pending question is one module-level value, and this drew it wherever the panel happened
         * to be: the agent tree, a model page, a different conversation. So a question from one thread
         * followed the reader around the whole app, arriving under screens that had nothing to do with
         * it and offering options about a model they were no longer looking at.
         *
         * A question with no conversation on it still shows — that is a caller with genuinely no thread
         * (a tool driven outside a conversation), and silently hiding it would strand the promise
         * waiting behind it forever.
         */
        if (ask.conversationId && ask.conversationId !== this.conversation?.id)
            return nothing;
        const send = (text) => {
            const t = text.trim();
            if (!t)
                return;
            this.askDraft = '';
            answerAsk(ask.id, t);
        };
        return html `<div class="ai-ask">
      <div class="ai-ask-q">${renderMarkdown(ask.question, copyCode)}</div>
      ${ask.options.length
            ? html `<div class="ai-ask-opts">
              ${
            // PLAIN buttons, not vitesse-button: a suggested answer is a whole sentence, and it
            // must WRAP to the panel width rather than run off the right edge under the app chrome.
            // vitesse-button is nowrap by contract; these are full-width and wrap.
            ask.options.map((o) => html `<button class="ai-ask-opt" @click=${() => send(o)}>${o}</button>`)}
            </div>`
            : nothing}
      <div class="ai-ask-reply">
        <input
          class="ai-ask-input"
          .value=${this.askDraft}
          placeholder=${t('ai.askPlaceholder')}
          @input=${(e) => (this.askDraft = e.target.value)}
          @keydown=${(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(this.askDraft);
            }
        }}
        />
        <vitesse-button size="sm" icon="send" @click=${() => send(this.askDraft)}>${t('ai.send')}</vitesse-button>
      </div>
    </div>`;
    }
    render() {
        const p = this.place;
        if (!p || p.k === 'hub')
            return nothing;
        return html `${this.renderStatus()}
    ${p.k === 'viewer'
            ? this.renderViewer(p.runId)
            : p.k === 'tree'
                ? this.renderTree()
                : p.k === 'run'
                    ? html `${this.renderChrome()}${this.renderRun()}`
                    : /*
                       * A THREAD'S BACK IS THE STACK'S BACK, not a hard-coded way out.
                       *
                       * `renderChat` defaults its back to "leave for the list", which was right when the panel
                       * showed one place: the way out of a top-level thread is the recents. In a stack it is
                       * wrong for every column but the last one — pressing back on a context column emptied the
                       * trail and dropped the reader at the list, losing the two columns they were working in.
                       *
                       * So it asks the panel, exactly as the run and canvas surfaces do: close this column if
                       * there is something behind it, and only leave when this thread IS the whole trail.
                       */
                        html `${this.renderChrome()}${this.renderChat(() => (this.canBack ? this.back() : this.leaveThread()))}`}${this.renderAsk()}`;
    }
    /**
     * BACK AND FORWARD, in one place, for every surface.
     *
     * Rendered at the panel rather than inside each surface because the thing being navigated is
     * recursive — a sub-agent's chat is a chat, its canvas is a canvas — and the way out of the
     * fourth level should be the same control in the same spot as the way out of the first. The
     * per-surface "back" buttons remain for the one-step case; this is what makes the trail
     * traversable in both directions.
     *
     * Absent until there is somewhere to go, so an ordinary single-thread session never sees it.
     */
    /**
     * ONE CHROME ROW, holding the trail's controls and whatever the current surface offers beside
     * them.
     *
     * They were two stacked rows — back/forward, then the view pills — which on a side rail is over
     * sixty pixels of chrome above content that is already short of room, and it grew every time a
     * surface wanted its own switch. They are also the same KIND of control: where am I, and how am I
     * looking at it. One row, nav on the left, the surface's own tabs beside it.
     *
     * Rendered even when the nav is absent, because the tabs still need somewhere to live; the row
     * disappears entirely when neither has anything to show.
     */
    /*
     * The back/forward pair that used to live here is GONE.
     *
     * It rendered only when the trail had somewhere to go, so it appeared and vanished as the reader
     * moved — pushing the content below it down and back up by its own height, which is the layout
     * shift it was reported as. Two glyphs that are absent most of the time also read as decoration
     * rather than as navigation, so the cost was being paid for something nobody could rely on.
     *
     * The TRAIL itself stays: it is what makes back out of a sub-agent lead to the agent that spawned
     * it rather than to the conversations list, and each surface's own back button walks it. Only
     * FORWARD is lost with these, and re-entering is the same one click that got you there.
     */
    renderChrome(tabs = nothing) {
        if (tabs === nothing)
            return nothing;
        return html `<div class="ai-chrome">${tabs}</div>`;
    }
    /** A slim strip shown ONLY when something is wrong. Connecting and the initial handshake are
     *  deliberately silent: a sub-second auth+connect is normal and showing "Connecting…" for it just
     *  flashes a line the user cannot act on. If the connection actually FAILS, that they need to see. */
    renderStatus() {
        // A delete that did not land. Said here rather than swallowed: the row is about to reappear in
        // the list, and a thread coming back from the dead with no explanation reads as a bug.
        if (this.notice)
            return html `<div class="ai-strip ai-strip-bad">${this.notice}</div>`;
        return nothing;
    }
    /*
     * HAS THE SURFACE MOVED — the one bit of state the back-to-top control needs.
     *
     * Kept as state rather than read from the element on render, because the control appears and
     * disappears and Lit has to be told. rAF-throttled for the same reason `BasePage` throttles its
     * own sticky maths: a scroll event fires far more often than a frame, and the answer here changes
     * at most once per scroll gesture.
     */
    /**
     * Open a thread.
     *
     * `opening` is shown because RESUMING fetches a page of history before the transcript can render,
     * and a rail that sat still for that beat would look like the click missed.
     */
    /**
     * Put the panel in a place WITHOUT recording it — the move back and forward both make.
     *
     * Every surface is cleared before the target is set, because the render dispatch is a priority
     * chain: a stale `runId` left behind would win over the thread being returned to, and the reader
     * would press back and appear not to move.
     */
    async apply(p) {
        this.runId = '';
        this.treeRootRunId = '';
        switch (p.k) {
            case 'hub':
                return;
            case 'viewer':
                // The run is the address; the tree already holds it. Nothing to open and nothing to fetch —
                // which is why a viewer column paints on the first frame rather than showing a skeleton.
                this.manager?.runTree.watch(p.runId);
                this.watchTree();
                return;
            case 'run':
                this.runId = p.runId;
                await this.openRunConversation(p.runId);
                return;
            case 'tree':
                this.treeView = p.view;
                this.manager?.runTree.watch(p.root);
                this.watchTree();
                this.treeRootRunId = p.root;
                return;
            case 'chat':
                // Already open is the common case coming back from a canvas, and reopening would throw away
                // the live object — including anything streaming into it right now.
                if (this.conversation?.id !== p.id)
                    await this.choose(p.id);
                return;
        }
    }
    get canBack() {
        return this.nav?.canBack ?? false;
    }
    back() {
        this.nav?.back();
    }
    /**
     * Open the conversation a run is talking in, for the `run` place.
     *
     * A run id is the address (`AgentRun.conversation_id` is "open it and you are talking to the
     * agent"), so the conversation is resolved here rather than being carried in the link.
     *
     * NOT `choose`, which records a `chat` place on the trail: called from `apply` that would push an
     * entry for somewhere the reader never went, and back would then appear not to move — the same
     * class of bug the trail comment warns about for `runId`.
     */
    /**
     * The job this agent was given — for its own thread's head.
     *
     * Only for a RUN's thread. A person's conversation opens with what they typed; an agent's opened
     * with the agent already working and no sign of what was asked, which is the question the reader
     * came to answer. The objective is on the run, so it is read from there rather than invented.
     */
    /**
     * THE AGENT'S ANSWER AS IT IS BEING WRITTEN.
     *
     * A run's transcript is durable and lands a STEP at a time — the assistant's message appears when
     * the model call returns. For an agent that calls no tools that is the whole run, so the thread
     * showed nothing for the length of the call and then everything at once. The driver now publishes
     * the streaming text onto the RUN, which this view already re-reads every couple of seconds; this
     * is where it gets painted.
     *
     * Only while the run is UNFINISHED. The step's own progress write clears the field the moment the
     * durable message exists, but a stale read landing after that would otherwise put the ghost of a
     * sentence under the finished answer.
     */
    runPartial() {
        if (!this.runId)
            return EMPTY;
        const run = this.transcript?.run;
        // A run that has not been read back yet is not a run that is finished. The lookup can trail the
        // first chunks by seconds, and refusing to paint until it landed was a blank page for exactly
        // the opening of every agent.
        if (run && (runState(run) === 'done' || runState(run) === 'stopped'))
            return EMPTY;
        /*
         * THIS TAB FIRST, the run second — and they are two different sources for the same fact.
         *
         * A run spawned from a chat in this browser is DRIVEN by this browser, so its text is already
         * here, chunk by chunk, with no network in the way. A run driven anywhere else — the headless
         * lane, another tab — publishes the same text onto the run itself, which this view polls. The
         * local one is always fresher when it exists, so it wins.
         */
        const local = livePartial(this.runId);
        if (local.parts.length)
            return local;
        // A run driven ELSEWHERE — the headless lane, another tab — publishes onto the run itself.
        return run?.partial ? { text: run.partial, reasoning: '', tools: [], parts: [{ kind: 'text', text: run.partial }] } : EMPTY;
    }
    brief() {
        /*
         * ONLY FOR THE RUN'S OWN THREAD, and only while that thread is the one open.
         *
         * `runTranscript` holds the LAST run opened and keeps holding it — so stepping out of an agent
         * into a conversation you started yourself put that agent's brief at the top of YOUR thread,
         * signed "assigned by the agent that spawned this one". Nothing spawned it; you typed it. A
         * transcript that attributes a person's own blank conversation to an agent is the same class of
         * lie as drawing a return in the user's bubble, and it survived because the stale value was
         * still perfectly well-formed.
         *
         * Two guards, because they answer different questions: `runId` says this surface is an agent's
         * at all, and the conversation match says it is THIS agent's rather than the one before it.
         */
        if (!this.runId)
            return undefined;
        const run = this.transcript?.run;
        if (!run?.task?.objective)
            return undefined;
        if (run.conversationId && this.conversation?.id && run.conversationId !== this.conversation.id)
            return undefined;
        const parent = run.parentRunId ? this.manager?.runTree?.run(run.parentRunId) : undefined;
        return {
            objective: run.task.objective,
            deliverable: run.task.deliverableSpec ?? '',
            from: parent ? parent.task?.label || parent.expertId || '' : '',
        };
    }
    async openRunConversation(runId) {
        const transcript = this.transcript;
        if (!transcript || !this.manager)
            return;
        /*
         * TWO ROUND TRIPS THAT DID NOT HAVE TO BE IN SEQUENCE.
         *
         * This read the run to learn its conversation id, then opened that conversation — so every step
         * into an agent cost a full request before the second one could start, and the reader watched
         * "Opening…" for both. The id is only unknown when nothing has loaded the run yet: reaching an
         * agent from the tree, or from the spawn row that started it, means the tree is already in hand
         * and already holds it. When it does, the two reads overlap.
         */
        const knownRun = this.manager.runTree?.run(runId);
        const known = knownRun?.conversationId ?? '';
        /*
         * THE THREAD SWAPS FIRST — this is what caused the flash of the wrong agent.
         *
         * `apply` sets `runId` before this runs, so the panel is already rendering the RUN surface; and
         * this used to await the transcript before touching `conversation`, which still held the last
         * thread. For a whole round trip the reader was looking at an agent's header over the PREVIOUS
         * agent's transcript — a second later it swapped, which reads as the app changing its mind
         * about where it just took you.
         *
         * When the tree already holds the run (reaching an agent from the canvas, from the list, or
         * from a spawn row — which is nearly always) the right thread can be opened immediately and
         * paints its own skeleton. When it does not, the old thread is CLEARED rather than left up:
         * "loading" is honest and the previous agent's words are not.
         */
        /*
         * THE NAME IS CLEARED WHETHER OR NOT A THREAD IS UP.
         *
         * `convTitle` was only reset inside the branch below, which needs a conversation object to
         * enter — and leaving a thread for the list does not clear that object, it only stops showing
         * it. So opening an agent from the list, where the previous thread is gone from view but still
         * held, drew the PREVIOUS thread's name over the new agent's skeleton for the length of the
         * read. The title is its own state and has to be cleared on its own terms.
         */
        if (this.convTitle && !(this.conversation && this.conversation.id === known)) {
            this.convTitle = knownRun?.task?.label ?? '';
            this.chapters = [];
        }
        if (this.conversation && this.conversation.id !== known) {
            /*
             * THE WRONG THREAD COMES DOWN BEFORE THE RIGHT ONE GOES UP, and this is the half the first
             * attempt at this fix missed. Opening the known thread up front was not enough: `open` is
             * still an await — it dynamic-imports the conversation module on the first call — and for
             * those frames the surface went on rendering the thread that was already there. Observed
             * live, clicking a sub-agent from the canvas: the PARENT's title over a loading skeleton,
             * then the agent's. Exactly the "it shows me the spawner first" this set out to remove.
             *
             * So it is cleared unconditionally when it is not already the right one. `renderRun` then
             * says "Opening…", which is a true statement about a thread that is a moment away; the
             * previous agent's name over the next agent's transcript is not a true statement about
             * anything.
             */
            this.conversation = null;
            this.watchConversation(null);
            this.watchRunAudits('');
        }
        /*
         * OPENING SPANS THE WHOLE OPEN, and this is what put a reader in a dead end.
         *
         * It was set inside the branch that already KNEW the conversation — so a run the tree has not
         * loaded (a click straight from the list, a restored link) cleared the old thread, left
         * `opening` false, and then awaited the transcript. For that whole round trip `renderRun` saw no
         * conversation and no open in progress, which is the one combination that means "this agent
         * never started" — a sentence about a run, said about a read.
         */
        this.opening = true;
        try {
            if (known) {
                this.conversation = await this.manager.open(known);
                this.watchConversation(this.conversation ?? null);
                this.watchRunAudits(known);
                /*
                 * AND ITS NAME ARRIVES WITH IT.
                 *
                 * The header reads `convTitle`, which was only written once the TRANSCRIPT came back — a
                 * second round trip — so the agent's own skeleton was drawn under the previous agent's
                 * name. Clearing the thread alone did not fix that: the title is separate state and it
                 * stayed stale. The tree already holds this run and a run's label is what heads its thread,
                 * so there is nothing to wait for.
                 */
                this.convTitle = knownRun?.task?.label ?? '';
                this.adoptSpecialist(knownRun);
            }
            await transcript.open(runId);
            const run = transcript.run;
            const conversationId = run?.conversationId ?? known;
            /*
             * A run with no conversation has not STARTED — queued, or spawned and not yet picked up. That
             * is a different thing from an agent that has said nothing, and it is the only case where
             * there is genuinely no thread to show; the view says so rather than rendering an empty chat.
             */
            if (!conversationId) {
                this.conversation = null;
                this.watchConversation(null);
                this.watchRunAudits('');
                this.convTitle = run?.task?.label ?? '';
                return;
            }
            if (this.conversation?.id === conversationId)
                return;
            this.conversation = await this.manager.open(conversationId);
            this.watchConversation(this.conversation ?? null);
            this.watchRunAudits(conversationId);
            // The agent's NAME heads its thread. A sub-agent's conversation has no user-authored title,
            // so falling back to the conversations list would leave the header blank.
            this.convTitle = run?.task?.label ?? '';
            this.adoptSpecialist(run);
            this.chapters = [];
            if (this.conversation)
                void this.chapterizeAgent(this.conversation.id, run, transcript.turns.length);
        }
        finally {
            this.opening = false;
        }
    }
    /**
     * AN AGENT'S THREAD IS THAT SPECIALIST'S THREAD — so the composer says so.
     *
     * The tools menu reads `conversation.expertId`, and an agent's conversation was opened with it
     * empty: a reader inside a PLC-program specialist's thread opened the `+` menu and saw
     * "Conversation" selected, on a surface where every word above it had been written by a
     * different specialist. Typing there then ran the reply as the CONVERSATIONAL agent — the wrong
     * roster, the wrong prompt, the wrong tools — in the middle of a specialist's work.
     *
     * The run has carried `expertId` all along; nothing handed it across. Handed across here, with
     * the MODEL and EFFORT that belong to it, for the same reason `pickSpecialist` applies both when
     * a person chooses one: a specialist is a model and a reasoning budget as much as it is a prompt,
     * and half of it is a specialist that does not behave like itself.
     *
     * Only when the run names one — an un-specialised agent stays on the thread's own settings.
     */
    adoptSpecialist(run) {
        const id = run?.expertId ?? '';
        if (!id || !this.conversation)
            return;
        this.conversation.expertId = id;
        const spec = (this.manager?.specialists ?? []).find((e) => e.id === id);
        if (!spec)
            return; // a roster that has moved on: keep the id, leave the tuning alone
        if (spec.model)
            this.conversation.model = spec.model;
        if (spec.effort)
            this.conversation.effort = spec.effort;
    }
    /**
     * Give an agent's thread the contents rail its parent gets for free.
     *
     * A thread the browser drives re-segments on a stride inside `send()`. An agent's thread never
     * passes through `send()` — the worker writes all of it — so it never reaches that cadence and
     * shows no contents at all, however long the agent ran. Reading the chapters and, when there are
     * none, asking for them is what closes that gap; the "chapters" event brings the answer back.
     *
     * ONLY for a run that has ENDED, and only ONCE. A pass is a billed model call, and the contents
     * of a still-running agent would describe a thread still being written — its own finish is when
     * the shape is final. The set is per-tab, so returning to an agent reads the chapters it already
     * has instead of buying them a second time.
     *
     * The turn count comes from the TRANSCRIPT, which was read above, and NOT from the conversation:
     * `open` hydrates in the background so a thread's own turns are still empty here, and reading
     * them would mean every agent looked too short to have contents.
     */
    async chapterizeAgent(conversationId, run, turns) {
        await this.loadChapters(conversationId);
        if (this.chapters.length || this.segmented.has(conversationId))
            return;
        if (run?.status !== FINISHED || turns < CHAPTER_FLOOR_TURNS)
            return;
        this.segmented.add(conversationId);
        await this.manager?.segmentConversation(conversationId);
    }
    /**
     * Fetch them for the thread now open.
     *
     * Once per open, not per render: chapters change when somebody pins one or the segmenter runs,
     * both of which already refresh through their own paths. Re-fetching on every render would put a
     * request behind every keystroke in the composer.
     */
    async loadChapters(conversationId) {
        this.chapters = await (this.manager?.listChapters(conversationId) ?? Promise.resolve([]));
    }
    versionsAt(seq, firstSeq) {
        const id = this.conversation?.id;
        const rows = this.manager?.conversations.conversations;
        if (!id || !rows)
            return null;
        if (this.branchIndex?.rows !== rows)
            this.branchIndex = { rows, ix: indexBranches(rows) };
        const p = positionAt(this.branchIndex.ix, id, seq, firstSeq);
        return p ? { index: p.index, count: p.count, prev: p.prev, next: p.next } : null;
    }
    /*
     * THE ONE CHAT SURFACE — a parent's thread and a sub-agent's are the same render.
     *
     * A sub-agent used to get its own read-only view, and every difference the engineer reported came
     * from that split rather than from anything about sub-agents: no live stream (so the status sat on
     * "Running" and the elapsed time only moved when the preview was reopened), no composer, no usage
     * or token badges, and "this agent has not said anything yet" for a run that was in fact talking.
     * Fixing those one at a time would have meant re-implementing the chat surface next to itself, and
     * the two would drift again.
     *
     * So there is one definition and two call sites. The only thing that varies is where BACK goes —
     * out of a thread is the hub; out of a sub-agent is the agent that spawned it, which the trail
     * already knows. A parent and a child cannot look different because there is nothing to differ.
     */
    /*
     * ARTIFACTS ARE FETCHED, NEVER LINKED — and this note lives HERE, above the template, because a
     * comment inside the opening tag is not a comment.
     *
     * A `//` line inside an `html` template is literal TEXT, and this one contained `<img`. The HTML
     * parser opened a new element at that point, so every `.property` binding after it landed on a
     * child instead of on `<vitesse-ai-chat>` — `.onBack`, `.onOpenTree`, `.onOpenConversation` and
     * three model defaults, all silently unbound. The panel rendered, the buttons drew, and nothing
     * they called existed: no back chevron, and clicking through to a spawned agent did nothing at
     * all. Everything type-checked and every test passed, because the defect is in a string.
     *
     * The note itself: the download route is authed and re-checks the org on every read, so an
     * `img src` — which cannot carry a bearer token — will not work. The bytes come through the AI
     * plane, which holds the token, and become an object URL in the document.
     *
     * TWO MORE NOTES THAT USED TO SIT INSIDE THE TAG, moved here for the same reason. They were
     * `/* … *\/` blocks, which are not comments in markup either — they became sixty-five junk
     * attributes on a live element, invisible only because none of them happened to contain a `<`.
     *
     *   · `agentNames` — the run tree is the only place a run id becomes a name, and the panel is
     *     the only layer that holds one, so a message from a peer is named here or nowhere.
     *   · `onOpenConversation` — "open this agent's chat". The inline tree called it from the day it
     *     was written and the panel never bound it, so the button rendered, looked live, and did
     *     nothing. The mirror of the `onOpenTree` defect: there a parent bound what no child
     *     declared; here a child declared what no parent bound. Both failed silently, which is why
     *     both survived — and why the check in `lit-template-comments.test.ts` exists.
     */
    renderChat(onBack = this.leaveThread, agent) {
        return html `<vitesse-ai-chat
      density=${this.density}
      .agent=${agent}
      .brief=${this.brief()}
      .streaming=${this.runPartial()}
      .conversation=${this.conversation}
      .loadArtifact=${(f) => this.conversation ? (this.manager?.readArtifact(this.conversation.id, f.sha256) ?? Promise.resolve(null)) : Promise.resolve(null)}
      .specialists=${this.manager?.specialists ?? []}
      convTitle=${this.convTitle}
      .userName=${this.userName}
      .confirms=${this.manager?.pendingConfirms ?? []}
      .onConfirm=${(id, approve) => this.manager?.resolveConfirm(id, approve)}
      .onDelete=${this.deleteOpen}
      .onBack=${onBack}
      .onBackHover=${() => this.manager?.conversations.prefetch()}
      .surface=${this.manager?.surface ?? ''}
      .defaultChatModel=${this.manager?.current.textModel ?? ''}
      .defaultVoiceModel=${this.manager?.current.model ?? ''}
      .defaultImageModel=${this.manager?.current.imageModel ?? ''}
      .versionsAt=${(seq, firstSeq) => this.versionsAt(seq, firstSeq)}
      .chapters=${this.chapters}
      .onStartVoice=${() => this.manager?.dispatchEvent(new CustomEvent('start-voice'))}
      .onOpenVersion=${(id) => this.nav?.visit({ k: 'chat', id })}
      .onBranchSubmit=${async (seq, text) => {
            const from = this.conversation?.id;
            if (!from)
                return;
            const id = await this.manager?.branchConversation(from, seq, text);
            // Land IN the branch, and take the turn there — the person edited a question in order to
            // ask it, so stopping at "a version now exists" would leave them to press send twice.
            if (id) {
                // The branch already HOLDS the edited message — the server appended it when it made the
                // version. Opening it hydrates that, so the turn is taken with the question already in
                // place; sending it again would ask it twice.
                await this.choose(id);
                this.nav?.replaceCurrent({ k: 'chat', id });
                await this.conversation?.send(text, 'again');
            }
        }}
      .onForkTurn=${(seq) => {
            const from = this.conversation?.id;
            if (!from)
                return;
            void (async () => {
                const id = await this.manager?.forkConversation(from, seq);
                if (id)
                    this.nav?.visit({ k: 'chat', id });
            })();
        }}
      .onPinTurn=${(seq, title) => {
            const id = this.conversation?.id;
            if (!id)
                return;
            // Re-read after the write, so the contents button appears on the pin rather than on the
            // next time the thread is opened — the person pinned it in order to navigate by it.
            void this.manager?.pinChapter(id, title, seq).then(() => this.loadChapters(id));
        }}
      .nodeRunId=${this.runId}
      .treeRoot=${this.runId}
      .agentNames=${(runId) => this.manager?.runTree?.run(runId)?.task?.label ?? ''}
      .openArtifact=${(runId) => {
            // Resolved from the tree, which already holds every run this thread spawned — so the control
            // is decided on the same fact the viewer column will read, and cannot promise what it lacks.
            const run = this.manager?.runTree?.run(runId);
            if (!artifactOf(run?.ret?.deliverableJson ?? ''))
                return undefined;
            return () => this.nav?.visit({ k: 'viewer', runId });
        }}
      .agentRunning=${(runId) => {
            const run = this.manager?.runTree?.run(runId);
            return run ? run.status !== RunStatus.FINISHED : undefined;
        }}
      .onOpenTree=${(rootRunId, opts) => {
            /*
             * THE FLIGHT IS FOR THE SURFACE IT REPLACES.
             *
             * "Open this agent" travels out to the canvas, across to the card and into it — one gesture
             * that shows where the agent sits in the shape before dropping the reader inside it. That
             * journey earns its cost when the canvas is taking the column over: the reader is leaving
             * the thread either way, and the flight is what tells them where they went.
             *
             * Where a column opens BESIDE the thread, it earns nothing and costs a surface. The reader
             * clicked a row asking for the agent, and what arrived was the whole forest as a third
             * column wedged between the thread they were reading and the agent they wanted — a place
             * they have to close, in the middle of a stack, to get back to what they asked for.
             *
             * So the flight is kept for the one-column case and the stack opens the agent directly.
             * Nothing is lost: the canvas has its own control in the header, one click away, for the
             * reader who wants the shape rather than the agent.
             */
            const direct = this.nav?.stacks && opts?.enter && opts.focus;
            if (direct)
                return this.nav?.visit({ k: 'run', runId: opts.focus });
            /*
             * AND THE CANVAS OPENS IN THE COLUMN IT WAS ASKED FOR IN.
             *
             * It used to be a `visit`, which pushes — and the trail's head is the LEFTMOST column, so
             * pressing the tree in the middle of a stack opened a canvas on the far left and shoved
             * every column sideways. A control has to act where it was pressed; anywhere else is the
             * app answering a different question.
             *
             * `replaceCurrent` rewrites THIS column's place, and the origin rides along so leaving the
             * canvas hands the column back to the thread it was showing. Which is what the control has
             * always claimed to be: a toggle between two views of one conversation.
             */
            return this.nav?.replaceCurrent({
                k: 'tree',
                root: rootRunId,
                focus: opts?.focus ?? '',
                ...(opts?.from !== undefined ? { from: opts.from } : {}),
                ...(opts?.enter ? { enter: true } : {}),
                ...(this.place ? { origin: this.place } : {}),
                view: this.treeView,
            });
        }}
      .onOpenConversation=${(conversationId, runId) => 
        // Prefer the RUN place: it is the same chat surface, and it is the one that knows the
        // agent's name. Falling back to the conversation keeps callers that have no run id working.
        runId ? this.nav?.visit({ k: 'run', runId }) : this.nav?.visit({ k: 'chat', id: conversationId })}
    ></vitesse-ai-chat>`;
    }
    static { this.SETTLE_MS = 400; }
    editKey(runId, docId) {
        return `${runId}::${docId}`;
    }
    renderViewer(runId) {
        const run = this.manager?.runTree?.run(runId);
        const original = artifactOf(run?.ret?.deliverableJson ?? '');
        /*
         * THE ARTIFACT AS IT STANDS — the agent's, with whatever the reader has typed over it.
         *
         * The live tab renders THIS, so an edit runs the moment it is made. That is the point of being
         * able to type at all: a widget you can read but not try is a widget you are guessing about.
         *
         * A declaration that does not parse is ignored rather than thrown: the person is mid-keystroke,
         * the editor is already underlining the line, and blanking the preview on every half-typed brace
         * would make the tab flicker at exactly the moment they need to see it.
         */
        const made = original ? this.editedArtifact(runId, original, this.edits) : undefined;
        const running = original ? this.editedArtifact(runId, original, this.settled) : undefined;
        const title = made?.title || (run ? runLabel(run) : '') || t('ai.viewerRender');
        // The LIVE tab runs the settled text, not the half-typed one — see `settled`.
        const preview = running ? this.manager?.artifactPreview?.(running) : undefined;
        // The host's own editor, when it has one — see `artifactCode` on the manager.
        const code = this.manager?.artifactCode;
        const tabs = made ? viewerTabs(made.docs, Boolean(preview)) : [];
        const now = viewerTab(this.viewerPick, tabs);
        const doc = made?.docs.find((d) => d.id === now);
        const bad = doc ? (this.problems[this.editKey(runId, doc.id)] ?? []) : [];
        const changed = made?.docs.some((d) => this.edits[this.editKey(runId, d.id)] !== undefined) ?? false;
        /*
         * THE PICKER LIVES IN THE ONE HEADER THIS SURFACE HAS.
         *
         * The viewer used to draw its own bordered tab bar directly beneath this row: a second
         * header-shaped band for a surface that already has one, and the pills inside it came out
         * unstyled — `<vitesse-pill-tabs>` is a light-DOM element that takes its look from the app's
         * global pill stylesheet, and a shadow root is exactly where that stylesheet does not reach.
         * Rendered here, in the panel's light DOM, it is the same bar every page in the product uses,
         * on the row the surface already had.
         */
        const head = html `<div class="ai-head">
      ${headLead({
            onBack: () => this.back(),
            backLabel: this.previousLabel(),
            title,
            icon: 'monitor-play',
        })}
      <!-- WHAT IS WRONG WITH IT, next to what it is. The editor underlines the line; a reader who
           has scrolled away from it still needs to know there is one, and a reader about to hand the
           thing to the assistant needs to know before they do. -->
      ${bad.length ? html `<span class="ai-head-bad" title=${bad.map((b) => `${b.line}: ${b.message}`).join('\n')}>${statusBadge('red', t('ai.viewerProblems', { count: bad.length }))}</span>` : nothing}
      ${doc || changed
            ? html `<div class="ai-head-actions">
              <!-- The way an edit becomes real: it goes to the thread that made it, as the reader's
                   own next message, for them to send. Not sent for them — handing somebody's draft
                   to a model on their behalf is a decision only they can make.

                   On EVERY tab, not only the documents: the live tab is where you decide the edit is
                   good, and the control to act on it has to be there when you decide. -->
              ${changed
                ? html `<button
                      class="base-button base-button-blue"
                      title=${t('ai.viewerHandOverHint')}
                      @click=${() => this.handEditToAgent(runId, made)}
                    >
                      <vitesse-icon name="corner-down-left" size="14"></vitesse-icon>
                      <span>${t('ai.viewerHandOver')}</span>
                    </button>`
                : nothing}
              ${doc
                ? html `<button
                      class="base-button base-button-blue"
                      title=${t('common.copy')}
                      @click=${() => this.copyDoc(doc.text, doc.id)}
                    >
                      <vitesse-icon name=${this.copied === doc.id ? 'badge-check' : 'copy'} size="14"></vitesse-icon>
                      <span>${this.copied === doc.id ? t('common.copied') : t('common.copy')}</span>
                    </button>`
                : nothing}
            </div>`
            : nothing}
    </div>`;
        if (!made) {
            return html `${this.renderChrome(head)}
        <div class="base-empty">${t('ai.viewerEmpty')}</div>`;
        }
        /*
         * THE PICKER SITS UNDER THE HEADER, NOT IN IT.
         *
         * Two placements were wrong before this one. Inside the viewer it was a bordered bar of its own
         * — a second header-shaped band under the real one. Inside the header it crowded a 52px row that
         * already carries a chevron, a title and an action, and wrapped to a second line in a narrow
         * column. Here it is a row of pills on the page's own ground: no border, no band, nothing that
         * reads as chrome. The sticky header stays the one header this surface has.
         */
        return html `${this.renderChrome(head)}
      ${tabs.length > 1
            ? html `<div class="ai-viewer-tabs">
              <vitesse-pill-tabs .tabs=${tabs} active=${now} .onChange=${(id) => (this.viewerPick = id)}></vitesse-pill-tabs>
            </div>`
            : nothing}
      <vitesse-source-viewer class="ai-stage" .docs=${made.docs} ?has-live=${Boolean(preview)} ?host-doc=${Boolean(code)} active=${now}>
        ${preview ? html `<div slot="live" class="ai-artifact">${preview}</div>` : nothing}
        ${code && doc
            ? html `<div slot="doc" class="ai-doc">
                ${code({
                ...doc,
                editable: true,
                onEdit: (text) => {
                    this.edits = { ...this.edits, [this.editKey(runId, doc.id)]: text };
                    // …and the live tab follows once the typing stops. See `settled`.
                    if (this.settleTimer)
                        clearTimeout(this.settleTimer);
                    this.settleTimer = setTimeout(() => {
                        this.settleTimer = null;
                        this.settled = { ...this.edits };
                    }, VitesseAiSurface_1.SETTLE_MS);
                },
                onProblems: (found) => {
                    this.problems = { ...this.problems, [this.editKey(runId, doc.id)]: found };
                },
            })}
              </div>`
            : nothing}
      </vitesse-source-viewer>`;
    }
    /**
     * The artifact with the reader's edits applied — see `edits`.
     *
     * Rebuilt from the edited SOURCE and the edited DECLARATION rather than patched, so the two tabs
     * and the live tab are always looking at one object. A declaration that does not parse is skipped
     * and the last good one stands: the person is mid-keystroke and the editor is already saying so.
     */
    editedArtifact(runId, base, from) {
        const src = from[this.editKey(runId, 'source')];
        const decl = from[this.editKey(runId, 'declaration')];
        if (src === undefined && decl === undefined)
            return base;
        let rest = { title: base.title, config: base.config };
        if (decl !== undefined) {
            try {
                const parsed = JSON.parse(decl);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
                    rest = parsed;
            }
            catch {
                /* half-typed — keep the last good declaration; the editor is already underlining it */
            }
        }
        const source = src ?? base.source;
        return {
            source,
            title: typeof rest.title === 'string' ? rest.title : base.title,
            config: rest.config ?? base.config,
            docs: [
                { id: 'source', label: base.docs[0]?.label ?? 'Source', text: source, lang: 'js' },
                { id: 'declaration', label: base.docs[1]?.label ?? 'Declaration', text: decl ?? base.docs[1]?.text ?? '', lang: 'json' },
            ],
        };
    }
    /**
     * HAND THE EDIT TO THE AGENT THAT MADE IT.
     *
     * The run's return stays exactly as it was — it is the record of what the agent did, not a draft —
     * so an edit reaches the model the way anything else does: as the reader's own next message in the
     * thread that produced it. Written into the draft rather than sent, because sending somebody's
     * work to a model is their decision and the composer is where they make it. The column opens on
     * the thread with the message already typed.
     */
    handEditToAgent(runId, made) {
        if (!made)
            return;
        const conversationId = this.manager?.runTree?.run(runId)?.conversationId ?? '';
        const surface = this.manager?.surface ?? '';
        const body = [t('ai.viewerHandOverPrompt'), '', '```js', made.source, '```', '', '```json', made.docs[1]?.text ?? '', '```'].join('\n');
        if (conversationId && surface)
            saveDraft(surface, conversationId, body);
        this.nav?.visit({ k: 'run', runId });
    }
    copyDoc(text, id) {
        void navigator.clipboard?.writeText(text).catch(() => { });
        this.copied = id;
        setTimeout(() => {
            if (this.copied === id)
                this.copied = '';
        }, 1400);
    }
    /** One agent's record. Back returns to whatever was underneath — the thread, or the hub. */
    /**
     * The tree, as its own surface.
     *
     * Back returns to the RECORD it was opened from rather than closing both: the tree is a detour
     * taken to get one's bearings, and dropping the reader at the thread would undo the navigation
     * they were in the middle of. Opening a node replaces the record underneath, so back always leads
     * one step out rather than to wherever this started.
     */
    renderTree() {
        const counts = this.manager?.runTree?.counts ?? { total: 0, running: 0, awaiting: 0, queued: 0, finished: 0 };
        const spend = forestUsage(this.manager?.runTree?.roots ?? []);
        const totals = { tokens: spend.tokens, costUsd: spend.costUsd + (this.conversation?.metrics.costUsd ?? 0) };
        // A run id, or none at all — the origin card is a conversation and has no run behind it, so it
        // opens as one. A fabricated run id there would send the reader to a record that does not exist.
        /*
         * A PLACE, EITHER WAY — and that is why the conversation card would not open.
         *
         * A run went through `visit`, which clears `treeRootRunId` on the way and so leaves the canvas.
         * The conversation went through `choose`, which opens the thread and does NOT — and `render`
         * checks `treeRootRunId` first, so the thread was opened, correctly, underneath a canvas that
         * stayed on screen. Clicking the card looked like a dead control and was in fact a surface
         * that never got out of the way.
         */
        const enter = (runId, conversationId) => {
            if (runId)
                this.nav?.visit({ k: 'run', runId });
            else if (conversationId)
                this.nav?.visit({ k: 'chat', id: conversationId });
        };
        /*
         * PIPES | TRACE | CHAT, as the mockup has it.
         *
         * Chat is not a third rendering of the tree — it is the thread this detour was taken from, so
         * the tab that leads back to it IS the back action. Spelling it as a tab rather than only as a
         * "back" link is what makes the three surfaces read as one place with three views instead of a
         * stack you have to unwind.
         */
        /*
         * ONE TOGGLE, because there are two views and they are two sides of a coin.
         *
         * There were three tabs — pipes, trace, chat — over two genuinely different things: a SHAPE and
         * a THREAD. Trace was a third rendering of the shape as ordered rows, which is what the inline
         * rows under a spawn already are, so it earned a tab by existing rather than by answering a
         * question the other two could not. Rows remain where they are useful: inside the spawn that
         * produced them.
         *
         * Chat and tree are mutually exclusive views of one conversation, so the control says which one
         * you are NOT looking at and switches to it — a toggle, not a tablist, because a tablist implies
         * a set you pick from and there is no third option to pick.
         */
        /*
         * THE SAME HEAD EVERY OTHER SURFACE HAS.
         *
         * This page had a lone tab strip: no title, no back, and a different vertical rhythm from the
         * thread it is reached from — so stepping between them moved the content up and down and left
         * the reader without the one control they wanted, which is out. Same 52px, same chevron, same
         * placement; the icon and the name say which surface this is.
         */
        /*
         * A NUMBER, AND A WORD THAT CAN GO.
         *
         * "6 agents" is the right badge in a header with room for it and the wrong one in a 260px
         * column, where the four badges together overflowed the row and were cut off by the wall — the
         * measurements were on screen and unreadable, which is worse than absent. Split so the unit
         * word drops first and the figure stays: "6", "2", "1.2M" still say what changed, and the full
         * text is on the badge's own title for anyone who wants it spelled out.
         */
        const metric = (n, unit) => html `${n}<span class="u">&nbsp;${unit}</span>`;
        const here = this.place;
        const place = here?.k === 'tree' ? here : undefined;
        const tabs = html `<div class="ai-head">
      ${headLead({
            /*
             * BACK OUT OF A CANVAS HANDS THE COLUMN BACK.
             *
             * The canvas took this column over rather than opening beside it, so leaving it is not a
             * step down the trail — it is the other half of the toggle that opened it. The place it
             * replaced rode along in `origin`; without one (a deep link straight to a canvas) there is
             * nothing to hand back and the ordinary back applies.
             */
            onBack: () => (place?.origin ? this.nav?.replaceCurrent(place.origin) : this.back()),
            // WHERE BACK ACTUALLY GOES. It said "Conversations" and went one step back down the
            // trail — which from an agent's canvas is the thread you came from, not the list. The
            // label now names the place the button leads to, so the tooltip and the destination are
            // the same fact.
            backLabel: this.previousLabel(),
            /*
             * "AGENT TREE", AND "TREE" WHEN THERE IS NO ROOM FOR BOTH.
             *
             * Two spellings of one name rather than a name and an abbreviation: the narrow form is
             * the wide form with its first word removed, so collapsing the header moves the title by
             * one word instead of swapping it for something that reads as a different surface. Both
             * are rendered and the container query below picks; a title chosen in script would have
             * to measure the column, which is the thing CSS is already doing.
             */
            title: html `<span class="name-full">${t('ai.pipes')}</span><span class="name-short">${t('ai.pipesShort')}</span>`,
            icon: 'network',
        })}
      <!-- WHAT IS ON THIS CANVAS, as the badges every other page carries. A tree of one agent and
             a tree of twenty are the same picture until you have panned it; the count says which
             before you start. Running is called out separately because it is the one number that
             changes while you are looking.

             IN ONE GROUP THAT CAN GIVE WAY, exactly as a thread's header does it. They were loose
             children of the row, each refusing to shrink, so a narrow column simply drew them past
             its own wall and under the column beside it. A group with a min-width of zero is what
             stops the row overflowing at all; the container queries then drop whole badges from the
             right rather than leaving one sliced in half. -->
      <div class="head-metrics">
        ${counts.total
            ? html `<span class="badge" title=${t('ai.subagentCount', { count: counts.total })}
                >${statusBadge('blue', metric(String(counts.total), t('ai.agentsUnit', { count: counts.total })))}</span
              >`
            : nothing}
        ${counts.running
            ? html `<span class="badge" title=${t('ai.runningCount', { count: counts.running })}
                >${statusBadge('green', metric(String(counts.running), t('ai.runningUnit')))}</span
              >`
            : nothing}
        <!-- WHAT THE WHOLE FAN-OUT HAS SPENT. The canvas is where somebody decides whether a tree
               is worth letting run, and that decision needs one number, not a card-by-card sum. Live:
               subtree usage is derived on every poll rather than rolled up when a child finishes, so
               this climbs while the agents are climbing. The conversation's own turns are IN the cost
               — the reader is being told what this piece of work costs, not what the agents cost. -->
        ${totals.tokens
            ? html `<span class="badge" title=${t('ai.tokenCount', { n: fmtTokenCount(totals.tokens) })}
                >${statusBadge('blue', metric(fmtTokenCount(totals.tokens), t('ai.tokensUnit')))}</span
              >`
            : nothing}
        ${totals.costUsd ? html `<span class="badge">${statusBadge('blue', fmtUsd(totals.costUsd))}</span>` : nothing}
      </div>
    </div>`;
        return html `${this.renderChrome(tabs)}
      <vitesse-run-pipes
        class="ai-stage"
        .tree=${this.manager?.runTree}
        .experts=${this.manager?.specialists}
        .origin=${this.treeOrigin()}
        .fromRunId=${place?.from ?? ''}
        .hasFrom=${place?.from !== undefined}
        .focusRunId=${place?.focus ?? ''}
        .onOpenRun=${enter}
        .onArrive=${(runId) => {
            if (!place)
                return;
            /*
             * ONCE, AND THEN NEVER AGAIN FOR THIS PLACE.
             *
             * "Travel here, and open it when you land" is true the first time the reader arrives and
             * false every time afterwards. Left on the trail it fired on every return: back out of the
             * agent and the canvas immediately threw you into it again, so the tree was a surface you
             * could not stand on. The focus goes with it — a canvas revisited should show the forest,
             * not re-fly a journey you have already taken.
             *
             * Consumed by REWRITING where you are rather than recording a move, so back and forward
             * land exactly where they would have.
             */
            /*
             * THE ONE-SHOTS GO; WHERE THE READER CAME FROM STAYS.
             *
             * `enter` and `focus` are instructions for one arrival — "dive into this card when you
             * land", "travel to that one" — and leaving them armed fired them again on the way back,
             * so the canvas threw the reader straight into the agent they had just stepped out of.
             *
             * `from` is not an instruction, it is a FACT about this detour: the card the reader was
             * inside when they opened the canvas. Clearing it meant a second visit framed the forest
             * from nowhere in particular, which is the cut the whole animation exists to avoid. Kept,
             * so the canvas re-centres on that card every time it is shown.
             */
            this.nav?.replaceCurrent({ ...place, focus: '', enter: false });
            // Only when the place asked for it — a canvas opened to BE looked at must not slide out
            // from under the reader on arrival.
            if (place.enter && runId)
                this.nav?.visit({ k: 'run', runId });
        }}
      ></vitesse-run-pipes>`;
    }
    /**
     * THE CONVERSATION THIS FOREST GREW OUT OF, for the canvas's leftmost card.
     *
     * `spawned_from_conversation_id` is the field that exists precisely for this: a root agent's
     * `parent_run_id` is a synthetic turn id with no row behind it, so following that finds nothing,
     * and its own `conversation_id` is its OWN thread. Read off the roots rather than off the trail,
     * because the canvas can be reached from anywhere and the tree always knows where it came from.
     */
    treeOrigin() {
        const roots = this.manager?.runTree?.roots ?? [];
        const id = roots.map((r) => r.run.spawnedFromConversationId).find(Boolean);
        if (!id)
            return undefined;
        /*
         * THE NAME, from whichever source actually has it.
         *
         * The recents page is one page: a thread further down the archive than the store has loaded is
         * simply not in it, and naming the card "New chat" would be the canvas failing to recognise the
         * conversation the reader is standing in. When the origin IS the open thread — the usual case,
         * since the canvas is reached from it — the panel already holds its title.
         */
        const c = this.manager?.conversations.conversations.find((x) => x.id === id);
        const title = c?.title || (id === this.conversation?.id ? this.convTitle : '');
        /* WHAT WAS ASKED, in the person's own words: the thread's opening message. It is the honest
           analogue of an agent's TASK — the same question, one level up — and it is the one line that
           says why any of the agents to its right exist. */
        const task = c?.preview || (id === this.conversation?.id ? this.firstSaidInThread() : '');
        return {
            conversationId: id,
            title: conversationLabel({ title, preview: c?.preview ?? '' }),
            task,
            messages: c?.messageCount ?? this.conversation?.turns.length ?? 0,
        };
    }
    /** The opening message of the thread in hand, for a conversation the recents page has not loaded
     *  far enough down the archive to describe. */
    firstSaidInThread() {
        return this.conversation?.turns.find((x) => x.role === 'user' && !x.returned && !x.fromRunId)?.text ?? '';
    }
    /**
     * The name of the place BACK leads to.
     *
     * Read off the trail rather than hard-coded, because that is what back actually does: it walks one
     * step down the trail, and from an agent's canvas that step is usually the thread it was opened
     * from. A button labelled "Conversations" that lands on a conversation is a small lie told on
     * every surface that has one.
     */
    previousLabel() {
        return this.nav?.previousLabel ?? t('ai.backToConversations');
    }
    /**
     * One agent's thread — the SAME surface a person's own thread gets.
     *
     * Back leads to whoever spawned it rather than to the conversations list: a sub-agent was reached
     * from its parent, so that is where leaving it returns. The trail already holds that, which is why
     * this is `back()` and not a hard-coded destination.
     */
    renderRun() {
        /*
         * Spawned but not started yet: there is no conversation to open, so say that plainly. Rendering
         * an empty chat here would read as an agent that had nothing to say, which was the old view's
         * mistake and is the one thing a reader must not conclude from a run that has not begun.
         */
        if (!this.conversation) {
            /*
             * A SKELETON WHILE IT ARRIVES; the sentence only when there is genuinely nothing.
             *
             * These were told apart correctly and rendered the same way — as a line of text in an empty
             * panel — and one of the two sentences is a claim about an agent. Observed live: opening an
             * agent that had finished twenty minutes earlier said "This agent has not started yet" for as
             * long as the read took, which is not a slow load, it is a wrong statement about the thing
             * being loaded. A reader has no way to tell it apart from the real case.
             *
             * So a thread that is on its way draws the transcript's own skeleton — the same bars the chat
             * shows while it hydrates, so opening an agent looks like opening a thread rather than like an
             * error that later resolves. The sentence is kept for the case it was written for: a run that
             * really has not been picked up yet has no thread, and saying so is the whole answer.
             */
            /*
             * AND IT KEEPS ITS HEADER, WHICH IS THE HALF THAT TRAPPED PEOPLE.
             *
             * `renderChrome()` with no tabs renders NOTHING, so this branch drew a bare sentence on an
             * empty panel — no title, and no way out. A reader who reached it could not go back, could not
             * reach the list, and had to reload the page. That is worse than any wrong sentence, and it
             * was reachable by simply opening an agent whose run the tree had not loaded.
             *
             * So the head is drawn here too, with the same back the thread has. A surface that cannot show
             * you its contents must still let you leave.
             */
            const head = html `<div class="ai-head">
        ${headLead({
                onBack: () => (this.canBack ? this.back() : this.leaveThread()),
                backLabel: this.previousLabel(),
                title: this.convTitle || t('ai.runUntitled'),
                icon: 'sparkles',
            })}
      </div>`;
            if (this.opening)
                return html `${this.renderChrome(head)}${transcriptSkeleton()}`;
            return html `${this.renderChrome(head)}
        <div class="base-empty">${t('ai.runNotStarted')}</div>`;
        }
        /*
         * WHAT STATE THIS AGENT IS IN, beside what it spent.
         *
         * A person's thread has no such thing — it is idle whenever they are not typing. An agent's is
         * the first thing you want from the header, and the panel is where it can be known: the chat
         * surface is shared and has no idea it is showing a run.
         */
        const run = this.transcript?.run;
        /*
         * THE SERVER'S ANSWER, HANDED TO THE THREAD.
         *
         * A conversation cannot know which run it belongs to; this surface is the one place that does.
         * Written on every render rather than kept, so it is a MIRROR of the tree and not a second copy
         * of it — which is what makes it impossible for a reload to bring back a "thinking" thread whose
         * agent stopped an hour ago. Without it this tab runs the model on behalf of a dead agent: a
         * child finishing after its parent was stopped pushes its answer into the parent's thread and
         * the drain takes a turn to hand it over.
         */
        if (this.conversation)
            this.conversation.runFinished = !!run && runState(run) !== 'running' && runState(run) !== 'awaiting' && runState(run) !== 'queued';
        // Started here rather than on a lifecycle hook: this is the one place that knows the header is
        // showing a run at all, and a clock started anywhere else would be guessing.
        this.tickClock();
        return this.renderChat(
        /*
         * BACK ALWAYS LEADS SOMEWHERE.
         *
         * An agent is normally reached from its parent, so back walks the trail — that is the whole
         * point of the trail. But an agent can also be the FIRST place in a session: the panel
         * restores the last conversation on open, and if that conversation is an agent's, the trail
         * holds one entry and `back()` is a no-op. The reader is then inside a sub-agent's transcript
         * with a back button that does nothing, and the only way out is a page reload.
         *
         * At the bottom of the trail the way out is the list, which is where leaving a thread has
         * always led.
         */
        () => (this.canBack ? this.back() : this.leaveThread()), run
            ? {
                state: runState(run),
                elapsed: runElapsed(run),
                cost: runCost(run).replace(' · ', ''),
                tokens: runTokens(run),
                onStop: () => void this.stopRun(run.runId),
                // Only when it actually made something — `artifactOf` is undefined for an answer that is
                // a sentence, so the door appears on exactly the agents that have one behind it.
                ...(artifactOf(run.ret?.deliverableJson ?? '')
                    ? { onOpenArtifact: () => this.nav?.visit({ k: 'viewer', runId: run.runId }) }
                    : {}),
            }
            : undefined);
    }
    /** The owner's stop: this run and everything beneath it. A refusal is shown, not swallowed. */
    async stopRun(runId) {
        const refused = await this.manager?.runTree.abort(runId, t('ai.stoppedByYou'));
        if (refused)
            this.notice = refused;
        // A question the stopped run was waiting on can no longer be answered — the run that asked it
        // is gone — so it leaves the screen with the run rather than waiting for a reply nobody can use.
        cancelAsk(runId);
        void this.transcript?.refreshRun();
    }
};
__decorate([
    property({ attribute: false })
], VitesseAiSurface.prototype, "manager", void 0);
__decorate([
    property()
], VitesseAiSurface.prototype, "userName", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiSurface.prototype, "place", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiSurface.prototype, "nav", void 0);
__decorate([
    property({ reflect: true })
], VitesseAiSurface.prototype, "density", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "askDraft", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "conversation", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "opening", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "runId", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "treeRootRunId", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "treeView", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "convTitle", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "notice", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "chapters", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "viewerPick", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "edits", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "settled", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "problems", void 0);
__decorate([
    state()
], VitesseAiSurface.prototype, "copied", void 0);
VitesseAiSurface = VitesseAiSurface_1 = __decorate([
    customElement('vitesse-ai-surface')
], VitesseAiSurface);
export { VitesseAiSurface };
//# sourceMappingURL=surface.js.map